import type {
  ExternalWorkIntegrationStatusValues,
  JiraCustomFieldFilter,
  WorkIntegration_Keys,
} from '@/lib/api/types/work-integrations';
import type { IntegrationSourceField } from '@/lib/api/work-integrations/integrationItemSourceFields';

export const workIntegrationsQueryKeys = {
  workIntegrations: ['workIntegrations'],
  workIntegrationsAll: (apiOptions?: {
    limit?: number;
    filter?: {
      statuses?: ExternalWorkIntegrationStatusValues[];
      ids?: number[];
      loopIds?: number[];
    };
  }) => ['workIntegrations', apiOptions],
  jiraInstances: () => ['workIntegrations', 'instances'],
  jiraInstanceIssueTypes: ({ instanceId }: { instanceId?: string }) => [
    'workIntegrations',
    'jiraInstanceIssueTypes',
    { instanceId },
  ],
  jiraItemLevelMappings: ({ instanceId }: { instanceId?: string }) => [
    'workIntegrations',
    'jiraItemLevelMappings',
    { instanceId },
  ],
  jiraItemTypeMappings: ({ instanceId }: { instanceId?: string }) => [
    'workIntegrations',
    'jiraItemTypeMappings',
    { instanceId },
  ],
  jiraFields: ({
    oauthCredentialsId,
    instanceId,
    projectId,
  }: {
    oauthCredentialsId?: number;
    instanceId?: string;
    projectId?: string;
  }) => ['workIntegrations', 'jiraFields', { oauthCredentialsId, instanceId, projectId }],
  jiraCustomFields: ({
    credentialsId,
    instanceId,
    projectId,
  }: {
    credentialsId?: number;
    instanceId?: string;
    projectId?: string;
  }) => ['workIntegrations', 'jiraCustomFields', { credentialsId, instanceId, projectId }],
  jiraFieldOptions: ({
    credentialsId,
    instanceId,
    fieldId,
    fieldValue,
  }: {
    credentialsId?: number;
    instanceId?: string;
    fieldId?: string;
    fieldValue?: string;
  }) => [
    'workIntegrations',
    'jiraFieldOptions',
    { credentialsId, instanceId, fieldId, fieldValue },
  ],
  jiraProjects: ({ cloudId }: { cloudId?: string }) => [
    'workIntegrations',
    'jiraProjects',
    { cloudId },
  ],
  jiraIssueTypesAndStatuses: ({
    oauthCredentialsId,
    cloudId,
    projectIdOrKey,
  }: {
    oauthCredentialsId?: number;
    cloudId?: string;
    projectIdOrKey?: string;
  }) => [
    'workIntegrations',
    'jiraIssueTypesAndStatuses',
    { oauthCredentialsId, cloudId, projectIdOrKey },
  ],
  jiraIssueTypesWithHierarchy: ({
    oauthCredentialsId,
    cloudId,
    projectIdOrKey,
  }: {
    oauthCredentialsId?: number;
    cloudId?: string;
    projectIdOrKey?: string;
  }) => [
    'workIntegrations',
    'jiraIssueTypesWithHierarchy',
    { oauthCredentialsId, cloudId, projectIdOrKey },
  ],
  jiraWorkItemCount: ({
    cloudId,
    projectId,
    typeIds = [],
    statusIds = [],
    includeWorkAfter,
    jql,
    customFieldFilters = [],
  }: {
    cloudId?: string;
    projectId?: string;
    typeIds?: string[];
    statusIds?: string[];
    includeWorkAfter?: string;
    jql?: string;
    customFieldFilters?: JiraCustomFieldFilter[];
  }) => [
    'jiraWorkItemCount',
    {
      cloudId,
      projectId,
      typeIds,
      statusIds,
      includeWorkAfter,
      jql,
      customFieldFilters,
    },
  ],
  jiraJqlValidation: ({
    oauthCredentialsId,
    cloudId,
    jql,
  }: {
    oauthCredentialsId?: number;
    cloudId?: string;
    jql?: string;
  }) => ['workIntegrations', 'jiraJqlValidation', { oauthCredentialsId, cloudId, jql }],
  workItemPreview: ({ projectId }: { projectId?: string }) => [
    'workIntegrations',
    'workItemPreview',
    { projectId },
  ],
  workIntegrationErrors: ({ workIntegrationId }: { workIntegrationId?: number }) => [
    'workIntegrations',
    'errors',
    { workIntegrationId },
  ],
  workIntegrationStatus: () => ['workIntegrations', 'status'],
  workIntegrationItems: ({ itemIds }: { itemIds?: Set<number> }) => [
    'workIntegrations',
    'items',
    { itemIds: itemIds ? Array.from(itemIds).sort() : undefined },
  ],
  schedules: ({ itemIds }: { itemIds?: Set<number> }) => [
    'schedules',
    'items',
    { itemIds: itemIds ? Array.from(itemIds).sort() : undefined },
  ],
  scheduleByItemId: (itemId: number) => ['schedules', 'single', itemId] as const,
  integrationSchedule: ({ integrationId }: { integrationId: number | undefined }) =>
    ['workIntegrations', 'schedule', { integrationId }] as const,
  estimations: {
    byIds: (estimationIds: Set<number>) => [
      'estimations',
      'byIds',
      { estimationIds: estimationIds.size > 0 ? Array.from(estimationIds).sort() : undefined },
    ],
  },
  integrationInstances: (sourceSystemType?: WorkIntegration_Keys) => [
    'workIntegrations',
    'registered-instances',
    { sourceSystemType },
  ],
  instanceFieldMappings: ({
    numericInstanceId,
    projectId,
  }: {
    numericInstanceId?: number;
    projectId?: string;
  }) => ['workIntegrations', 'instanceFieldMappings', { numericInstanceId, projectId }],
  instanceFieldMappingUsage: ({ numericInstanceId }: { numericInstanceId?: number }) => [
    'workIntegrations',
    'instanceFieldMappingUsage',
    { numericInstanceId },
  ],
  integrationCustomFieldMappings: ({ integrationId }: { integrationId?: number }) => [
    'workIntegrations',
    'integrationCustomFieldMappings',
    { integrationId },
  ],
  oauthCredentialStatuses: ({ credentialIds }: { credentialIds: number[] }) => [
    'workIntegrations',
    'oauthCredentialStatuses',
    { credentialIds },
  ],
  integrationItemSourceFields: ({
    instanceId,
    issueKeyOrId,
    fields,
  }: {
    instanceId?: string;
    issueKeyOrId?: string;
    fields?: IntegrationSourceField[];
  }) => [
    'workIntegrations',
    'integrationItemSourceFields',
    { instanceId, issueKeyOrId, fields: fields ? [...fields].sort() : undefined },
  ],
};
